import { gql } from '@apollo/client';
import {
  Box,
  chakra,
  Checkbox,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Grid,
  GridItem,
  Heading,
  HStack,
  Stack,
  useToast,
} from '@chakra-ui/react';
import { ErrorMessage } from '@hookform/error-message';
import { zodResolver } from '@hookform/resolvers/zod';
import { Permission } from '@tp-vision/roles-permissions';
import { t } from 'i18next';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import Select, { OptionProps, SingleValue } from 'react-select';
import { z } from 'zod';
import { useAuth } from '~auth/useAuth';
import { ConfigurationStatusType } from '~components/configurations/ConfigStateToggle';
import { DisplayProperty } from '~components/displays/DisplayDetail/connectivity/ConnectivitySection';
import { FormLabelPendingIndicator } from '~components/ui/FormLabelPendingIndicator';
import { InfoIcon } from '~components/ui/icons';
import { components, selectOptionBaseSchema } from '~components/ui/Select';
import {
  ConfigFirmwareVersionSelect_ConfigFragment,
  useConfigUpdateFirmwareToLatestFlagMutation,
  useConfigUpdateFirmwareVersionMutation,
} from './__generated__/ConfigFirmware.graphql';
import { checkStateToDisableTab } from './ConfigDetailSettings';
import { useConfigDetailSettingsTab } from './ConfigDetailView';

const FirmwareVersionOption = selectOptionBaseSchema;

type FirmwareVersionOption = z.TypeOf<typeof FirmwareVersionOption>;

const schema = z.object({
  configId: z.string(),
  FirmwareVersion: FirmwareVersionOption.optional().nullable(),
});

type FormValues = z.TypeOf<typeof schema>;

export function ConfigFirmwareSelect({
  config,
  configStatus,
}: {
  config: ConfigFirmwareVersionSelect_ConfigFragment;
  configStatus?: ConfigurationStatusType;
}) {
  const options = useMemo<FirmwareVersionOption[]>(
    () =>
      config.supportedFirmwares.map<FirmwareVersionOption>((p) => ({
        label: p,
        value: p,
      })),
    [config],
  );
  const latestFirmware = options?.[0];

  const toast = useToast();
  const currentFirmwareVersion = useMemo(() => {
    const selectedVersion = config.firmware?.scaler?.version
      ? config.firmware?.android.version + ' + ' + config.firmware.scaler.version
      : config.firmware?.android.version;
    return options.find((o) => o.value === selectedVersion) || null;
  }, [config, options]);

  const {
    control,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    defaultValues: {
      configId: config.id,
      FirmwareVersion: currentFirmwareVersion,
    },
    resolver: zodResolver(schema),
  });

  function setInitialSelectedFirmware() {
    if (currentFirmwareVersion) {
      return currentFirmwareVersion;
    } else if (options?.length && config?.shouldUpdateToLatestFirmware) {
      return latestFirmware;
    } else return null;
  }

  const [selectedFirmware, setSelectedFirmware] = useState<SingleValue<FirmwareVersionOption>>(
    setInitialSelectedFirmware(),
  );

  const [updateFirmwareVersion, { loading }] = useConfigUpdateFirmwareVersionMutation();
  const [updateFirmwareVersionToLatestFlag, { loading: latestFirmwareLoading }] =
    useConfigUpdateFirmwareToLatestFlagMutation();

  useEffect(() => {
    if (currentFirmwareVersion) {
      setSelectedFirmware(currentFirmwareVersion);
    }
  }, [currentFirmwareVersion]);

  const handleChange = async (selected: FirmwareVersionOption | null) => {
    if (!selected) {
      return;
    }

    try {
      await updateFirmwareVersion({
        variables: {
          input: {
            configurationId: config.id,
            targetVersion: selected.value,
          },
        },
        refetchQueries: ['CustomerConfigDetailPage'],
        awaitRefetchQueries: true,
      });
    } catch (err) {
      toast({
        status: 'error',
        title: t('errorUpdatingFirmwareVersion'),
      });
    }
  };

  const handleFirmwareUpdateToLatestFlagChange = async (checked: boolean) => {
    try {
      if (!checked) {
        const hasFirmwareVersion =
          config.firmware?.android?.version || config.firmware?.scaler?.version;

        if (!hasFirmwareVersion && latestFirmware) {
          setSelectedFirmware(latestFirmware);
          await updateFirmwareVersion({
            variables: {
              input: {
                configurationId: config.id,
                targetVersion: latestFirmware.value,
              },
            },
          });
        }
      }

      await updateFirmwareVersionToLatestFlag({
        variables: {
          input: {
            configurationId: config.id,
            shouldUpdateToLatestFirmware: checked,
          },
        },
        refetchQueries: ['CustomerConfigDetailPage'],
        awaitRefetchQueries: true,
      });
    } catch (err) {
      toast({
        status: 'error',
        title: t('errorUpdatingFirmwareVersion'),
      });
    }
  };

  const { verifyUserPermissions } = useAuth();

  const isDisplaySettingsUpdateDisabled =
    checkStateToDisableTab(configStatus) ||
    !verifyUserPermissions([Permission.ConfigurationUpdate]);

  return (
    <GridItem
      colSpan={{
        base: 8,
        sm: 6,
        lg: 8,
        xl: 5,
      }}
    >
      <HStack paddingY="2" marginTop="6" marginBottom="10" gap={2}>
        <InfoIcon />
        <Heading fontSize="25" color="gray.900" _first={{ marginTop: '0 !important' }}>
          {t('firmware')}
        </Heading>
      </HStack>

      <FormControl isInvalid={Boolean(errors.FirmwareVersion)}>
        <FormLabel w={'100%'}>
          {t('selectFirmwareVersion')}
          <FormLabelPendingIndicator isPending={loading || latestFirmwareLoading} />
        </FormLabel>
        <Controller
          name="FirmwareVersion"
          control={control}
          render={({ field }) => (
            <Select
              value={
                config.shouldUpdateToLatestFirmware
                  ? { label: t('latestFirmwareVersion'), value: 'latest' }
                  : selectedFirmware ?? { label: t('firmwarenotavailable'), value: 'notavailable' }
              }
              options={options}
              components={{
                ...components,
                Option: ManageFirmwareSelectOption,
              }}
              onChange={(selected) => {
                field.onChange(selected);
                handleChange(selected);
              }}
              isLoading={isSubmitting}
              placeholder={t('select')}
              isMulti={false}
              escapeClearsValue={false}
              isClearable={false}
              isSearchable={true}
              menuPlacement="auto"
              isDisabled={
                isDisplaySettingsUpdateDisabled || Boolean(config.shouldUpdateToLatestFirmware)
              }
            />
          )}
        />
        <ErrorMessage
          errors={errors}
          name="FirmwareVersion"
          render={({ message }) => <FormErrorMessage>{message}</FormErrorMessage>}
        />
      </FormControl>
      <Checkbox
        mt={4}
        isDisabled={isDisplaySettingsUpdateDisabled}
        isChecked={Boolean(config.shouldUpdateToLatestFirmware)}
        onChange={(e) => handleFirmwareUpdateToLatestFlagChange(e.target.checked)}
      >
        <strong>{t('autoUpdateToLatestFirmware')}</strong>
      </Checkbox>
      <Box mt={10}>
        <Grid templateColumns="repeat(2, 0.2fr)" gap={1}>
          {/* Left column */}
          <Stack align="start" spacing="5">
            <DisplayProperty
              label={t('configFirmware')}
              value={
                config.shouldUpdateToLatestFirmware
                  ? t('latestFirmwareVersion')
                  : selectedFirmware?.label ?? t('firmwarenotavailable')
              }
            />
            <DisplayProperty label={t('displayModel')} value={config.platform || t('unknown')} />
          </Stack>
        </Grid>
      </Box>
    </GridItem>
  );
}

export function ManageFirmwareSelectOption({
  isSelected,
  innerProps,
  ...rest
}: OptionProps<FirmwareVersionOption, false>) {
  const data = rest.data;

  return (
    <Box
      display="flex"
      alignItems="center"
      paddingX="3.5"
      paddingY="2"
      cursor="pointer"
      color={isSelected ? 'white' : 'blue.800'}
      background={isSelected ? 'blue.400' : 'white'}
      fontSize="md"
      _hover={{
        background: isSelected ? 'blue.400' : 'blue.50',
      }}
      {...innerProps}
    >
      <chakra.span display="inline-block" flex="1">
        {data.label}
      </chakra.span>
    </Box>
  );
}

export function ConfigFirmware() {
  const { configuration, configStatus } = useConfigDetailSettingsTab();
  return <ConfigFirmwareSelect config={configuration} configStatus={configStatus} />;
}

ConfigFirmwareSelect.graphql = {
  fragments: {
    ConfigFirmwareVersionSelect_config: gql`
      fragment ConfigFirmwareVersionSelect_config on ConfigurationModel {
        id
        shouldUpdateToLatestFirmware
        firmware {
          android {
            version
          }
          scaler {
            version
          }
        }
        platform
        supportedFirmwares
      }
    `,
  },
  mutations: {
    ConfigUpdateFirmwareVersion: gql`
      mutation ConfigUpdateFirmwareVersion($input: ConfigurationUpdateAndroidFirmwareInput!) {
        configurationUpdateAndroidFirmware(input: $input) {
          id
        }
      }
    `,
    ConfigUpdateFirmwareToLatestFlag: gql`
      mutation ConfigUpdateFirmwareToLatestFlag($input: ShouldUpdateToLatestFirmwareInput!) {
        configurationUpdateFirmwareToLatestFlag(input: $input) {
          id
        }
      }
    `,
  },
};
